import React, { useEffect, useMemo, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useGoogleLogin } from "@react-oauth/google";
import ClientLayout from "../../../layouts/ClientLayout";
import { useQueryClient } from "@tanstack/react-query";
import { authService } from "../../../lib/api/apiService";
import { useTranslation } from "react-i18next";
import { Eye, EyeOff } from "lucide-react";
import { getRecaptchaToken, preloadRecaptcha } from "../../../utils/recaptcha";
import RecaptchaLegalNotice from "../../../components/RecaptchaLegalNotice";

const createLoginSchema = (t) =>
  z.object({
    email: z
      .string()
      .trim()
      .min(1, t("auth.errors.email_required"))
      .email(t("auth.errors.email_invalid")),
    password: z
      .string()
      .min(1, t("auth.errors.password_required")),
  });

export default function UserLoginPage() {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const schema = useMemo(
    () => createLoginSchema(t),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [t, i18n.language]
  );

  const [googleLoading, setGoogleLoading] = useState(false);
  const [error, setError] = useState("");
  const [rememberMe, setRememberMe] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(schema),
    defaultValues: {
      email: "",
      password: "",
    },
  });

  const finalizeLogin = useCallback(
    (accessToken, user) => {
      if (!accessToken || !user) {
        setError(t("auth.errors.missing_info"));
        return;
      }

      if (rememberMe) {
        localStorage.setItem("access_token", accessToken);
        localStorage.setItem("user", JSON.stringify(user));
        localStorage.setItem("remember_me", "true");
        sessionStorage.removeItem("access_token");
        sessionStorage.removeItem("user");
      } else {
        sessionStorage.setItem("access_token", accessToken);
        sessionStorage.setItem("user", JSON.stringify(user));
        localStorage.removeItem("access_token");
        localStorage.removeItem("user");
        localStorage.removeItem("remember_me");
      }

      queryClient.setQueryData(["current-user"], user);
      queryClient.invalidateQueries({ queryKey: ["current-user"] });

      switch (user.role) {
        case "CLIENT":
          navigate("/");
          break;
        case "SELLER":
          navigate("/dashboard");
          break;
        case "ADMIN":
          navigate("/admin-dashboard");
          break;
        default:
          navigate("/");
      }
    },
    [rememberMe, navigate, t, queryClient]
  );

  useEffect(() => {
    preloadRecaptcha();
  }, []);

  useEffect(() => {
    const remembered = localStorage.getItem("remember_me") === "true";
    if (remembered) {
      setRememberMe(true);
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        try {
          const parsedUser = JSON.parse(storedUser);
          if (parsedUser?.email) {
            setValue("email", parsedUser.email);
          }
        } catch (parseError) {
          console.warn(
            "Impossible de parser l'utilisateur sauvegardé",
            parseError
          );
        }
      }
    }
  }, [setValue]);

  const inputBase =
    "w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-[#E5502E] outline-none";
  const inputClass = (hasError) =>
    `${inputBase} ${hasError ? "border-red-500 focus:ring-red-200" : ""}`;

  const onSubmit = handleSubmit(async (formData) => {
    setError("");

    try {
      const recaptchaToken = await getRecaptchaToken("login");
      const result = await authService.login(
        formData.email.trim().toLowerCase(),
        formData.password,
        recaptchaToken
      );

      if (!result) {
        setError(t("auth.errors.invalid_response"));
        return;
      }

      if (result.success === false) {
        setError(result.error || t("auth.errors.invalid_credentials"));
        return;
      }

      const statusCode = result.statusCode ?? result.status ?? result.code;
      const payload = result.data ?? result.payload ?? {};
      const accessToken =
        payload.access_token ?? payload.accessToken ?? result.access_token;
      const user = payload.user ?? result.user;

      if (
        (statusCode && String(statusCode) === "200") ||
        result.success === true ||
        (accessToken && user)
      ) {
        finalizeLogin(accessToken, user);
      } else {
        setError(result.message || t("auth.errors.invalid_credentials"));
      }
    } catch (err) {
      setError(
        err?.message?.includes("reCAPTCHA") || err?.message?.includes("Token")
          ? err.message
          : t("auth.errors.login_error")
      );
      console.error(err);
    }
  });

  const handleGoogleLoginSuccess = async (response) => {
    setGoogleLoading(true);
    setError("");

    try {
      const result = await authService.loginWithGoogle(response.access_token);

      if (!result) {
        setError(t("auth.errors.invalid_response"));
        return;
      }

      if (result.success === false) {
        setError(result.error || t("auth.errors.google_error"));
        return;
      }

      const statusCode = result.statusCode ?? result.status ?? result.code;
      const payload = result.data ?? result.payload ?? {};
      const accessToken =
        payload.access_token ?? payload.accessToken ?? result.access_token;
      const user = payload.user ?? result.user;

      if (
        (statusCode && String(statusCode) === "200") ||
        result.success === true ||
        (accessToken && user)
      ) {
        finalizeLogin(accessToken, user);
      } else {
        setError(result.message || t("auth.errors.google_error"));
      }
    } catch (err) {
      setError(t("auth.errors.google_error"));
      console.error(err);
    } finally {
      setGoogleLoading(false);
    }
  };

  const handleGoogleLoginError = () => {
    setError(t("auth.errors.google_auth_error"));
    setGoogleLoading(false);
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleLoginSuccess,
    onError: handleGoogleLoginError,
  });

  return (
    <ClientLayout>
      <div className="pt-">
        <div className="w-full">
          <div
            className="pt-12 pb-20 flex items-center justify-center bg-cover bg-center relative"
            style={{
              backgroundImage: "url('/images/family-6398107_1280.jpg')",
            }}
          >
            <div className="absolute inset-0 bg-[#F6F6F6] opacity-[0.96]"></div>
            <div className="relative max-w-lg w-full px-8">
              <h1 className="text-3xl font-poppins font-bold mb-6 flex justify-center whitespace-nowrap">
                {t("auth.welcome_title")}{" "}
                <span className="text-[#E5502E] ml-2">kilimmanjaro</span>
              </h1>

              {error && (
                <div className="mb-4 p-3 bg-red-100 border border-red-300 text-red-700 rounded-lg text-sm">
                  {error}
                </div>
              )}

              <form onSubmit={onSubmit} className="space-y-4" noValidate>
                <div>
                  <label className="block mb-1 text-start text-[#141C1D] text-[16px] font-semibold">
                    {t("auth.email_label")}
                  </label>
                  <input
                    type="email"
                    autoComplete="email"
                    {...register("email")}
                    className={inputClass(!!errors.email)}
                    placeholder={t("auth.email_placeholder")}
                  />
                  {errors.email && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.email.message}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block mb-1 text-start text-[#141C1D] text-[16px] font-semibold">
                    {t("auth.password_label")}
                  </label>
                  <div className="relative">
                    <input
                      type={showPassword ? "text" : "password"}
                      autoComplete="current-password"
                      {...register("password")}
                      className={`${inputClass(!!errors.password)} pr-11`}
                      placeholder={t("auth.password_placeholder")}
                    />
                    <button
                      type="button"
                      tabIndex={-1}
                      onClick={() => setShowPassword((prev) => !prev)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 p-1.5 text-gray-500 hover:text-gray-700 border-0 bg-transparent shadow-none outline-none focus:outline-none focus:ring-0 focus-visible:ring-0"
                      aria-label={
                        showPassword
                          ? t("auth.hide_password")
                          : t("auth.show_password")
                      }
                    >
                      {showPassword ? (
                        <EyeOff className="w-5 h-5" />
                      ) : (
                        <Eye className="w-5 h-5" />
                      )}
                    </button>
                  </div>
                  {errors.password && (
                    <p className="mt-1 text-sm text-red-600">
                      {errors.password.message}
                    </p>
                  )}
                </div>

                <div className="flex items-center justify-between text-sm text-gray-600">
                  <label className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={() => setRememberMe(!rememberMe)}
                      className="accent-[#E5502E]"
                    />
                    {t("auth.remember_me")}
                  </label>
                  <span
                    onClick={() => navigate("/reset-password")}
                    className="text-[#E5502E] font-medium cursor-pointer hover:underline"
                  >
                    {t("auth.forgot_password")}
                  </span>
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || googleLoading}
                  className="w-full bg-[#E5502E] text-white py-2 rounded-full hover:bg-[#cc4527] transition font-medium disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isSubmitting
                    ? t("auth.login_loading")
                    : t("auth.login_button")}
                </button>

                <RecaptchaLegalNotice className="text-center" />

                <button
                  type="button"
                  onClick={() => googleLogin()}
                  disabled={googleLoading || isSubmitting}
                  className="w-full border border-gray-300 font-medium text-gray-700 py-2 rounded-full flex items-center justify-center hover:bg-gray-100 transition disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {googleLoading ? (
                    t("auth.login_loading")
                  ) : (
                    <>
                      <img
                        src="/images/google-icon.png"
                        alt="Google"
                        className="w-5 h-5 mr-2"
                      />
                      {t("auth.login_with_google")}
                    </>
                  )}
                </button>
              </form>

              <p className="mt-4 text-center font-medium text-sm text-gray-600">
                {t("auth.no_account")}{" "}
                <span
                  onClick={() => navigate("/register")}
                  className="text-[#E5502E] font-medium cursor-pointer hover:underline"
                >
                  {t("auth.create_account")}
                </span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </ClientLayout>
  );
}
